/**
 * Category suggestions, one per dimension.
 *
 * A question is classified along several independent trees, so Leo proposes
 * for each tree on its own rather than searching one merged list and hoping
 * the right tree wins. Suggestions are never applied; the author adds or
 * dismisses each one.
 *
 * This is the local stand-in for the model call (`suggestQuestionClassification`
 * needs a key and the network). It reads the stem against each category's name,
 * its ancestors and its description, with a small clinical vocabulary so a stem
 * that says "QRS" still finds Cardiovascular. The shape it returns is what the
 * model path returns, so the UI does not change when that is wired in.
 */

import {
  categoriesInDimension,
  dimensionIdOfCategory,
  isDimensionRestricted,
  type CategoryDimension,
} from "./dimensions"
import { getCategoryAncestors, getChildCategories } from "./tree"
import type { ExamCategory } from "./types"

export type CategorySuggestion = {
  dimensionId: string
  categoryId: string
  /** 0 to 1. Shown as a word, not a number: the score is not that precise. */
  confidence: number
  /** One sentence naming what in the stem led here. */
  reason: string
  /** Narrows a category already assigned, instead of adding a new branch. */
  kind: "new" | "refinement"
}

const STOPWORDS = new Set([
  "which", "what", "when", "where", "while", "with", "that", "this", "these",
  "those", "their", "there", "from", "into", "have", "has", "had", "does", "will",
  "would", "should", "could", "about", "after", "before", "each", "most", "best",
  "first", "next", "following", "client", "nurse", "patient", "person", "than",
  "then", "them", "they", "also", "only", "such", "some", "more", "your", "being",
  "the", "for", "and", "are", "was", "were", "can", "not", "but", "his", "her",
  "its", "who", "how", "why", "any", "all", "one", "two", "per", "may", "must",
])

/** Stem words that point at category vocabulary the stem does not repeat. */
const VOCABULARY: Record<string, string[]> = {
  qrs: ["cardiovascular", "cardiac", "heart", "interpret"],
  "p wave": ["cardiovascular", "cardiac", "heart"],
  telemetry: ["cardiovascular", "cardiac", "heart", "assessment"],
  ecg: ["cardiovascular", "cardiac", "heart"],
  ekg: ["cardiovascular", "cardiac", "heart"],
  rhythm: ["cardiovascular", "cardiac", "heart"],
  pacemaker: ["cardiovascular", "cardiac", "heart"],
  atrial: ["cardiovascular", "cardiac", "heart"],
  ventricular: ["cardiovascular", "cardiac", "heart"],
  heart: ["cardiovascular", "cardiac"],
  murmur: ["cardiovascular", "cardiac", "heart"],
  oxygen: ["respiratory", "oxygenation", "pulmonary"],
  saturation: ["respiratory", "oxygenation", "pulmonary"],
  wheezing: ["respiratory", "pulmonary"],
  lung: ["respiratory", "pulmonary"],
  dose: ["dosage", "medication", "pharmacological", "pharmacology"],
  dosage: ["medication", "pharmacological", "pharmacology"],
  medication: ["pharmacological", "pharmacology", "medication"],
  drug: ["pharmacological", "pharmacology", "medication"],
  adverse: ["pharmacological", "pharmacology"],
  infection: ["infectious", "precautions", "infection"],
  isolation: ["infectious", "precautions", "transmission", "infection"],
  fever: ["infectious", "infection"],
  insulin: ["endocrine", "diabetes", "pharmacological"],
  glucose: ["endocrine", "diabetes"],
  stroke: ["neurologic", "neurology"],
  seizure: ["neurologic", "neurology"],
  prioritize: ["priorities", "management", "care"],
  delegate: ["delegation", "management", "supervision"],
  pediatric: ["pediatric", "growth", "development"],
  child: ["pediatric", "growth", "development"],
  pregnancy: ["women", "pregnancy", "maternal"],
  shock: ["physiologic", "adaptation", "shock"],
  fall: ["safety", "risk"],
  handoff: ["communicate", "teamwork", "collaboration"],
  error: ["safety", "near miss", "quality"],
}

function words(text: string): string[] {
  return (
    text
      .toLowerCase()
      .match(/[a-z][a-z'-]{2,}/g)
      ?.filter((word) => !STOPWORDS.has(word)) ?? []
  )
}

/** Light stemming so "precautions" matches "precaution". */
function root(word: string): string {
  return word.replace(/(ing|ed|es|s)$/u, "")
}

function stemSignal(stem: string) {
  const lower = stem.toLowerCase()
  const original = new Map<string, string>()
  for (const word of words(stem)) {
    if (!original.has(root(word))) original.set(root(word), word)
  }
  const direct = new Set(original.keys())
  const hinted = new Map<string, string>()
  for (const [trigger, hints] of Object.entries(VOCABULARY)) {
    if (!lower.includes(trigger)) continue
    for (const hint of hints) {
      hinted.set(root(hint), trigger)
    }
  }
  return { direct, hinted, original }
}

function categoryText(category: ExamCategory, categories: ExamCategory[]) {
  const ancestors = getCategoryAncestors(categories, category.id)
  return [
    category.name,
    category.description ?? "",
    ...ancestors.map((id) => categories.find((row) => row.id === id)?.name ?? ""),
  ].join(" ")
}

/**
 * Categories close to this one, nearest first: its siblings, then its parent's
 * siblings, and so on up. Used to fill a short list; they are not a reading of
 * the stem.
 */
function nearbyCategories(category: ExamCategory, pool: ExamCategory[], categories: ExamCategory[]): ExamCategory[] {
  const out: ExamCategory[] = []
  let parentId = category.parentId
  let cursor: ExamCategory | undefined = category
  while (cursor) {
    for (const other of pool) {
      if (other.parentId === cursor.parentId && other.id !== cursor.id && other.isActive && !out.includes(other)) out.push(other)
    }
    parentId = cursor.parentId
    cursor = parentId ? categories.find((row) => row.id === parentId) : undefined
  }
  return out
}

/** Needs enough words to read a topic out of. */
export function stemReadyForCategorySuggestions(stem: string): boolean {
  return stem.trim().split(/\s+/).filter(Boolean).length >= 6
}

type Scored = {
  category: ExamCategory
  score: number
  /** Matches in the category's own name. A leaf that matches only through its ancestors is not the answer. */
  ownHits: number
  matched: string[]
}

function scoreCategory(
  category: ExamCategory,
  categories: ExamCategory[],
  signal: ReturnType<typeof stemSignal>,
): Scored {
  const own = new Set(words(categoryText(category, categories)).map(root))
  const ownNameOnly = new Set(words(category.name).map(root))
  const matched: string[] = []
  let score = 0
  let ownHits = 0
  for (const token of own) {
    if (signal.direct.has(token)) {
      // A hit in the category's own name says more than one in an ancestor's.
      if (ownNameOnly.has(token)) ownHits += 1
      score += ownNameOnly.has(token) ? 2 : 1
      matched.push(signal.original.get(token) ?? token)
    } else if (signal.hinted.has(token)) {
      if (ownNameOnly.has(token)) ownHits += 1
      score += ownNameOnly.has(token) ? 1.4 : 0.7
      matched.push(signal.hinted.get(token)!)
    }
  }
  return { category, score, ownHits, matched }
}

export function suggestCategoriesByDimension(input: {
  stem: string
  categories: ExamCategory[]
  dimensions: readonly CategoryDimension[]
  /** What the question already carries. */
  assignedIds: readonly string[]
  /** Suggestions the author already turned down. */
  dismissedIds?: readonly string[]
  /** The program director can tag restricted standards; nobody else is offered them. */
  isDirector?: boolean
  /** How many to offer per tree, best first. Default one. Weaker matches fill the rest. */
  perDimension?: number
}): CategorySuggestion[] {
  const { stem, categories, dimensions, assignedIds } = input
  if (!stemReadyForCategorySuggestions(stem)) return []
  const signal = stemSignal(stem)
  if (signal.direct.size === 0 && signal.hinted.size === 0) return []
  const dismissed = new Set(input.dismissedIds ?? [])
  const assigned = new Set(assignedIds)
  // A parent of something already assigned is offered by the "broader
  // categories" prompt. Suggesting it again is the same ask twice.
  const coveredByPrompt = new Set(
    assignedIds.flatMap((id) => getCategoryAncestors(categories, id)),
  )
  const out: CategorySuggestion[] = []

  for (const dimension of dimensions) {
    if (isDimensionRestricted(dimension, Boolean(input.isDirector))) continue
    const pool = categoriesInDimension(categories, dimension.id)
    const scored = pool
      .filter(
        (category) =>
          !assigned.has(category.id) &&
          !dismissed.has(category.id) &&
          !coveredByPrompt.has(category.id),
      )
      .map((category) => scoreCategory(category, categories, signal))
      .filter((row) => row.score >= 2 && row.ownHits > 0)
      .sort((a, b) => b.score - a.score)
    const top = scored[0]
    if (!top) continue

    // Prefer the most specific node that scores nearly as well: a parent and
    // its child usually share the matching words, and the child is the answer.
    const specific =
      scored.find(
        (row) =>
          row.score >= top.score * 0.85 &&
          getCategoryAncestors(categories, row.category.id).length >
            getCategoryAncestors(categories, top.category.id).length,
      ) ?? top

    const picks: Scored[] = [specific]
    const overlaps = (row: Scored) =>
      picks.some(
        (other) =>
          other.category.id === row.category.id ||
          getCategoryAncestors(categories, other.category.id).includes(row.category.id) ||
          getCategoryAncestors(categories, row.category.id).includes(other.category.id),
      )
    const wanted = Math.max(1, input.perDimension ?? 1)
    // The next best distinct branches, then weaker partial matches, so there is
    // always something to choose between. A weak one says so in its confidence.
    for (const row of scored) {
      if (picks.length >= wanted) break
      if (!overlaps(row)) picks.push(row)
    }
    if (picks.length < wanted) {
      const weak = pool
        .filter((category) => !assigned.has(category.id) && !dismissed.has(category.id) && !coveredByPrompt.has(category.id))
        .map((category) => scoreCategory(category, categories, signal))
        .filter((row) => row.score > 0 && row.score < 2)
        .sort((a, b) => b.score - a.score)
      for (const row of weak) {
        if (picks.length >= wanted) break
        if (!overlaps(row)) picks.push(row)
      }
    }

    // Still short: categories beside the best match, in the same branch. Not a
    // reading of the stem, so they carry the lowest confidence.
    const nearby: Scored[] = []
    if (picks.length < wanted) {
      for (const category of nearbyCategories(picks[0]!.category, pool, categories)) {
        if (picks.length + nearby.length >= wanted) break
        if (assigned.has(category.id) || dismissed.has(category.id)) continue
        const row: Scored = { category, score: 0, ownHits: 0, matched: [] }
        if (overlaps(row) || nearby.some((n) => n.category.id === category.id)) continue
        nearby.push(row)
      }
    }
    for (const row of [...picks, ...nearby]) {
      const ancestors = getCategoryAncestors(categories, row.category.id)
      const refines = ancestors.some((id) => assigned.has(id))
      const matchedWords = [...new Set(row.matched)].slice(0, 3)
      out.push({
        dimensionId: dimension.id,
        categoryId: row.category.id,
        confidence: confidenceOfScore(row.score),
        reason:
          matchedWords.length > 0
            ? `The stem mentions ${matchedWords.map((word) => `"${word}"`).join(", ")}.`
            : nearby.includes(row)
              ? "Sits beside the closest match, in the same branch."
              : "The stem fits this category.",
        kind: refines ? "refinement" : "new",
      })
    }
  }
  return out
}

export function confidenceLabel(confidence: number): "High" | "Medium" | "Low" {
  if (confidence >= 0.8) return "High"
  if (confidence >= 0.5) return "Medium"
  return "Low"
}

export type MissingAncestors = {
  dimensionId: string
  /** Ancestors of assigned tags that the question does not carry yet. */
  categoryIds: string[]
}

/**
 * Parents the author has not added. A question tagged only with a child is
 * invisible to a blueprint line built on its parent, so the editor offers the
 * parent. It offers, never adds: in a reporting tree every tag is a deliberate
 * human decision.
 */
export function missingAncestorsByDimension(
  categories: ExamCategory[],
  assignedIds: readonly string[],
): MissingAncestors[] {
  const assigned = new Set(assignedIds)
  const byDimension = new Map<string, Set<string>>()
  for (const id of assignedIds) {
    const missing = getCategoryAncestors(categories, id).filter(
      (ancestorId) => !assigned.has(ancestorId),
    )
    if (missing.length === 0) continue
    const dimensionId = dimensionIdOfCategory(categories, id)
    const set = byDimension.get(dimensionId) ?? new Set<string>()
    for (const ancestorId of missing) set.add(ancestorId)
    byDimension.set(dimensionId, set)
  }
  return [...byDimension.entries()].map(([dimensionId, ids]) => ({
    dimensionId,
    categoryIds: [...ids],
  }))
}

/** Used by tree markers: does this node lead down to something suggested. */
export function nodeLeadsToSuggestion(
  categories: ExamCategory[],
  nodeId: string,
  suggestedIds: ReadonlySet<string>,
): boolean {
  const stack = [...getChildCategories(categories, nodeId)]
  while (stack.length > 0) {
    const next = stack.pop()!
    if (suggestedIds.has(next.id)) return true
    stack.push(...getChildCategories(categories, next.id))
  }
  return false
}

/** The same word reading the suggestion engine uses, for other matchers (the crosswalk). */
export const categoryWords = words
export const categoryWordRoot = root

/** "High confidence", "Medium confidence", "Low confidence": a word, not a number nobody can read. */
export function confidenceText(confidence: number): string {
  return `${confidenceLabel(confidence)} confidence`
}

function confidenceOfScore(score: number): number {
  return score >= 2 ? Math.min(0.97, 0.45 + score * 0.1) : Math.max(0.3, 0.3 + score * 0.075)
}

/**
 * How well one category fits a stem, on the same scale as the suggestions in
 * the category navigation. Used to put a confidence on a category a model chose.
 */
export function categoryConfidence(
  stem: string,
  category: ExamCategory,
  categories: ExamCategory[],
): number {
  return confidenceOfScore(scoreCategory(category, categories, stemSignal(stem)).score)
}

/**
 * The best few categories in one flat pool, for the surfaces that are handed a
 * catalog rather than the trees: Ask Leo and the question sheet. The same
 * reading of the stem as the navigation, so all three agree.
 */
export function suggestFromPool(input: {
  stem: string
  categories: ExamCategory[]
  assignedIds?: readonly string[]
  limit?: number
}): Array<{ categoryId: string; confidence: number; reason: string }> {
  const { stem, categories } = input
  if (!stemReadyForCategorySuggestions(stem)) return []
  const signal = stemSignal(stem)
  if (signal.direct.size === 0 && signal.hinted.size === 0) return []
  const assigned = new Set(input.assignedIds ?? [])
  const scored = categories
    .filter((category) => category.isActive && !assigned.has(category.id))
    .map((category) => scoreCategory(category, categories, signal))
    .filter((row) => row.score >= 2 && row.ownHits > 0)
    .sort((a, b) => b.score - a.score)
  const picked: typeof scored = []
  for (const row of scored) {
    if (picked.length >= (input.limit ?? 3)) break
    const lineage = new Set([row.category.id, ...getCategoryAncestors(categories, row.category.id)])
    // A parent and its child say the same thing: keep the first, the better scored.
    if (picked.some((other) => lineage.has(other.category.id) || getCategoryAncestors(categories, other.category.id).includes(row.category.id))) continue
    picked.push(row)
  }
  // Fewer than asked for: weaker partial matches fill the list, marked by a low confidence.
  if (picked.length < (input.limit ?? 3)) {
    const weak = categories
      .filter((category) => category.isActive && !assigned.has(category.id))
      .map((category) => scoreCategory(category, categories, signal))
      .filter((row) => row.score > 0 && row.score < 2)
      .sort((a, b) => b.score - a.score)
    for (const row of weak) {
      if (picked.length >= (input.limit ?? 3)) break
      const lineage = new Set([row.category.id, ...getCategoryAncestors(categories, row.category.id)])
      if (picked.some((other) => lineage.has(other.category.id) || getCategoryAncestors(categories, other.category.id).includes(row.category.id))) continue
      picked.push(row)
    }
  }
  if (picked.length > 0 && picked.length < (input.limit ?? 3)) {
    for (const category of nearbyCategories(picked[0]!.category, categories, categories)) {
      if (picked.length >= (input.limit ?? 3)) break
      if (assigned.has(category.id)) continue
      if (picked.some((other) => other.category.id === category.id)) continue
      picked.push({ category, score: 0, ownHits: 0, matched: [] })
    }
  }
  return picked.map((row) => {
    const words = [...new Set(row.matched)].slice(0, 3)
    return {
      categoryId: row.category.id,
      confidence: confidenceOfScore(row.score),
      reason: words.length > 0 ? `The stem mentions ${words.map((word) => `"${word}"`).join(", ")}.` : "Sits beside the closest match, in the same branch.",
    }
  })
}

/** The words in the stem that point at this category, for the "why" under a confidence. */
export function matchedWords(stem: string, category: ExamCategory, categories: ExamCategory[]): string[] {
  return [...new Set(scoreCategory(category, categories, stemSignal(stem)).matched)].slice(0, 4)
}

/**
 * Why a suggestion has the confidence it has, in plain lines. Shown behind the
 * info icon on the badge, so the word High, Medium or Low is never a bare claim.
 */
export function explainConfidence(input: {
  stem: string
  category: ExamCategory
  categories: ExamCategory[]
  confidence: number
  /** Where the suggestion came from. */
  source: "keywords" | "model" | "mapping" | "nearby"
  /** The model and the keywords both named it. */
  agreed?: boolean
  /** How well the model judged it to fit. */
  fit?: "high" | "medium" | "low"
  /** For a mapping: the content category it comes from, and whether the director confirmed it. */
  mappedFrom?: string
  mappingConfirmed?: boolean
  /** How people have treated this suggestion before. */
  feedback?: "skipped" | "added"
}): string[] {
  const lines: string[] = []
  const words = matchedWords(input.stem, input.category, input.categories)
  if (input.source === "mapping") {
    lines.push(
      input.mappingConfirmed
        ? `The program director confirmed that ${input.mappedFrom ?? "this content"} counts toward this standard.`
        : `Leo proposed that ${input.mappedFrom ?? "this content"} counts toward this standard; the director has not confirmed it yet.`,
    )
  } else if (input.source === "nearby") {
    lines.push("No words in the question match this category. It sits beside the closest match, so treat it as a fallback.")
  } else {
    if (words.length > 0) {
      lines.push(`The question mentions ${words.map((word) => `"${word}"`).join(", ")}, which are in this category's name or description.`)
    } else if (input.source === "model") {
      lines.push("Leo read the whole question and chose this category. No single word in the question gives it away.")
    }
    if (input.source === "model") {
      if (input.fit) lines.push(`Leo read the whole question and judged this a ${input.fit} fit.`)
      if (words.length > 0) {
        lines.push(input.agreed ? "The words in the question point the same way." : "The words alone were a weaker match; Leo chose it from the meaning.")
      }
    }
  }
  if (input.feedback === "skipped") lines.push("People have skipped this suggestion before, so it is shown lower.")
  if (input.feedback === "added") lines.push("People have added this suggestion before, so it is shown a little higher.")
  lines.push(
    input.confidence >= 0.8
      ? "High: several strong signals point here."
      : input.confidence >= 0.5
        ? "Medium: a real match, but check it against the question."
        : "Low: a weak match. Read it before adding.",
  )
  return lines
}
